import React, { Component } from 'react';
import moment from 'moment';
import { compose } from 'utils/compose';
import { inject, observer } from 'mobx-react';
import { BrowserPDF417Reader } from '@gojitsu/zxing-custom';
import CameraPhoto, { FACING_MODES } from 'jslib-html5-camera-photo';

import { IconButton, Button, Dialog, DialogContent, DialogTitle, Box, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { DialogFooter, DialogActionButton } from 'components/Dialog';

import { useIdScanStyles } from './styles';
import withMediaQuery from 'constants/mediaQuery';

// HOC to inject styles into class component
// no-dd-sa:javascript-code-style/parameter-name
const withUseStyles = (WrappedComponent) => {
  return function WithStylesWrapper(props) {
    const classes = useIdScanStyles();
    return <WrappedComponent {...props} classes={classes} />;
  };
};
const START_CAMERA_MAX_ATTEMPT = 10;

class ScanID extends Component {
  constructor(props) {
    super(props);
    this.cameraPhoto = null;
    this.videoRef = React.createRef();
    this.codeReader = new BrowserPDF417Reader();
    // this.codeReader = new BrowserQRCodeReader();
    this.state = {
      dataUri: '',
      alert: false,
      showCantscanDigalog: false,
      maxAttempt: 0,
    };
  }

  componentDidMount() {
    this.cameraPhoto = new CameraPhoto(this.videoRef.current);
    this.cameraPhoto.startCamera(FACING_MODES.ENVIRONMENT, { height: 1536, width: 450 }).then(() => this.scanOne());
  }

  processIdData = (content) => {
    const { onConfirmScan, store } = this.props;
    const settings = store?.deliveryStore?.settings;
    const ageLimit = settings?.id_required_age_limit ? parseInt(settings.id_required_age_limit, 10) : 21;

    let data = this.parsePDFContent(content);
    const isValidDOB = this.validateDOB(data['dob'], ageLimit);

    if (!isValidDOB) {
      this.openAlert();
    } else {
      onConfirmScan(data);
      this.goBack();
    }
  };

  scanOne = (e) => {
    if (this.state.maxAttempt > START_CAMERA_MAX_ATTEMPT) return;

    this.codeReader
      .decodeOnceFromVideoDevice(undefined, 'idvideo')
      .then((result, error) => {
        let content = result.text;
        this.processIdData(content);
      })
      .catch((error) => {
        this.setState({ maxAttempt: this.state.maxAttempt + 1 }, this.scanOne);
      });

    this.closeAlert();
  };

  scanContinue = (e) => {
    const that = this;
    this.codeReader
      .decodeFromInputVideoDeviceContinuously(undefined, 'idvideo', (result, err) => {
        if (result) {
          that.codeReader?.releaseAllStreams();
          this.cameraPhoto.stopCamera();
          let content = result.text;
          this.processIdData(content);
        }

        if (err) {
          /* empty */
        }
      })
      .catch((error) => {
        // alert('error is: ', error);
      });

    this.closeAlert();
  };

  reScan = () => {
    this.closeAlert();
    this.scanOne();
  };

  goBack = () => {
    const { navigate, location } = this.props;
    // this.cameraPhoto.stopCamera();
    navigate(location.pathname.replace(/\/scan[^\.]*$/g, '?token=' + this.props.token), { replace: true });
  };

  gotoShipment = () => {
    const { navigate, location } = this.props;
    navigate(location.pathname.replace(/\/idscan\/scan[^\.]*$/g, ''), { replace: true });
  };

  componentWillUnmount() {
    if (this.codeReader && typeof this.codeReader?.releaseAllStreams === 'function') this.codeReader.releaseAllStreams();
    if (this.cameraPhoto && typeof this.cameraPhoto?.stopCamera === 'function') {
      this.cameraPhoto
        .stopCamera()
        .then(() => {
          /* empty */
        })
        .catch((error) => {
          /* empty */
        });
    }
  }

  parsePDFContent = (content) => {
    let lines = content.split('\n');
    const data = { content };

    if (lines && lines.length > 0) {
      lines.forEach((line) => {
        if (line.startsWith('DBB')) {
          let dobString = line.substring(3);
          let dob = new Date(dobString.substring(4), parseInt(dobString.substring(0, 2), 10) - 1, dobString.substring(2, 4));
          data['dob'] = dob;
        }

        if (line.startsWith('DCS')) {
          data['last_name'] = line.substring(3);
        }
        if (line.startsWith('DAD') || line.startsWith('DBQ')) {
          data['middle_name'] = line.substring(3);
        }
        if (line.startsWith('DAC') || line.startsWith('DCT')) {
          data['first_name'] = line.substring(3);
        }
      });
    }

    return data;
  };

  validateDOB = (dob, ageLimit) => {
    let now = new Date();

    let age = now.getYear() - dob.getYear();

    if (now.getMonth() < dob.getMonth() || (now.getMonth() === dob.getMonth() && now.getDate() < dob.getDate())) {
      age -= 1;
    }

    if (age < ageLimit) {
      this.setState({ dob, ageLimit });

      return false;
    }

    return true;
  };

  startCameraMaxResolution = (facingMode, doScan) => {
    this.cameraPhoto
      .startCameraMaxResolution(facingMode)
      .then(() => {
        if (doScan) {
          this.scanOne();
        }
      })
      .catch((error) => {
        /* empty */
      });
  };

  closeAlert = (e) => {
    this.setState({ alert: false });
  };

  openAlert = (e) => {
    this.setState({ alert: true });
  };

  closeCantscanModal = (e) => {
    this.setState({ showCantscanDigalog: false });
  };

  openCantscanModal = (e) => {
    this.setState({ showCantscanDigalog: true });
  };

  driverScan = (e) => {
    this.setState({ showCantscanDigalog: false });
    this.gotoShipment();
  };

  render() {
    const { classes } = this.props;
    return (
      <Box sx={classes.container}>
        <Dialog open={this.state.alert} onClose={this.closeAlert} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
          <DialogTitle id="alert-dialog-title">ID Verification Failed</DialogTitle>
          <DialogContent>
            <Typography>
              Date of birth: <Typography component="span" sx={classes.dobText}>{moment(this.state.dob).format('MMM, D, YYYY')}</Typography>
            </Typography>
            <Typography>
              <Typography component="span" sx={classes.ageLimitText}>Recipient must be of age {this.state.ageLimit} or older.</Typography>
            </Typography>
          </DialogContent>
          <DialogFooter>
            <DialogActionButton onClick={this.goBack} color="primary" disableElevation>
              Close
            </DialogActionButton>
            <DialogActionButton onClick={this.reScan} color="primary" autoFocus disableElevation>
              ReScan
            </DialogActionButton>
          </DialogFooter>
        </Dialog>

        <Dialog
          open={this.state.showCantscanDigalog}
          onClose={this.closeAlert}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">Can't scan?</DialogTitle>
          <DialogContent>
            <Typography>Please try to place your card on a flat surface and hold your phone steady with both hands.</Typography>
            <Typography sx={classes.dialogSecondaryText}>
              If the ID still cannot be scanned, our driver must scan/verify your card in person.
            </Typography>
          </DialogContent>
            <Box sx={classes.dialogButtonWrapper}>
              <Button onClick={this.closeCantscanModal} sx={{ ...classes.button, ...classes.cantscanButton }} color="primary" variant="contained" disableElevation>
                Try scanning again
              </Button>
            </Box>
            <Box sx={classes.dialogButtonWrapperSecondary}>
              <Button onClick={this.driverScan} sx={{ ...classes.button, ...classes.cantscanButton }} variant="contained" color="secondary" disableElevation>
                Have driver collect
              </Button>
            </Box>
        </Dialog>

        <Box sx={classes.scanTipWrapper}>
          <IconButton onClick={this.goBack} sx={classes.backButton} size="small" aria-label="back">
            <ArrowBackIcon />
          </IconButton>
          <Box sx={classes.scanTip}>
            <Typography sx={classes.scanTipTitle}>Scan Identification Card</Typography>
            <Typography sx={classes.scanTipDesc}>Scan your identification card. Make sure to hold your phone steady.</Typography>
          </Box>
        </Box>
        <Box sx={classes.videoContainer}>
          <Box>
            <Box component="video" sx={classes.video} ref={this.videoRef} autoPlay={true} width="100%" id="idvideo" />
          </Box>
        </Box>
        <Box sx={classes.buttonContainer}>
          <Box sx={classes.dialogButtonWrapper}>
            <Button onClick={this.openCantscanModal} sx={{ ...classes.button, ...classes.cantscanButton }} variant="contained" disableElevation>
              Can't Scan?
            </Button>
          </Box>
        </Box>
        <Box sx={classes.tipContainer}>
          <Box sx={classes.scanTip}>
            <Typography sx={classes.scanTipDesc}>
              <Typography component="span" sx={classes.tipText}>TIP</Typography>: Try placing the card on a flat surface and holding your phone with both hands.
            </Typography>
            <Typography sx={classes.scanTipDesc}>
              Position your phone's camera to cover as much as possible the barcode on the back of your ID.
            </Typography>
          </Box>
        </Box>
        <Box>
          <Box component="img" src="https://ah-public-media.s3-us-west-2.amazonaws.com/images/IDSCAN-EXAMPLE.png" alt="" sx={classes.exampleImage} />
        </Box>
      </Box>
    );
  }
}

const ScanIDCompose = compose(inject('store'), observer)(ScanID);

export default withUseStyles(withMediaQuery()(ScanIDCompose));
